/**
 * RouteLoader — accessible skeleton shown during route transitions.
 * - Renders after defaultPendingMs (100ms) so instant nav feels instant.
 * - Announces loading to screen readers via role="status" + aria-live.
 * - Uses different logo asset for mobile vs laptop/desktop.
 * - Smooth fade-in avoids a hard flash on slow routes.
 */
export function RouteLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading page"
      className="fixed inset-0 z-[9999] flex flex-col bg-[#0a0a0c] animate-fade-in motion-reduce:animate-none"
    >
      {/* Top progress bar */}
      <div
        aria-hidden="true"
        className="h-[3px] w-full overflow-hidden bg-white/5"
      >
        <div className="h-full w-1/3 animate-[slide-in-right_1.2s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-red-500 to-transparent motion-reduce:hidden" />
      </div>

      {/* Centered brand mark */}
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          {/*
            Single small icon reused at both breakpoints. Using icon-192 on
            desktop upscaled via CSS costs ~15KB total instead of eagerly
            preloading both icon-192 (mobile) and icon-512 (~63KB, desktop),
            which React's SSR streams as `<link rel="preload">` even when the
            loader never actually mounts. `fetchPriority=low` prevents this
            transient asset from stealing the LCP hero's bandwidth window.
          */}
          <img
            src="/icon-192.png"
            alt=""
            width={192}
            height={192}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            className="block h-20 w-20 animate-pulse md:h-32 md:w-32"
          />

          <span className="text-xs uppercase tracking-[0.3em] text-white/60">
            Loading
          </span>
          <span className="sr-only">Loading, please wait…</span>
        </div>
      </div>

      {/* Skeleton scaffold below the fold (hints at page structure) */}
      <div
        aria-hidden="true"
        className="mx-auto w-full max-w-6xl px-6 pb-16 hidden md:block"
      >
        <div className="mb-4 h-6 w-2/5 animate-pulse rounded bg-white/5" />
        <div className="mb-8 h-4 w-1/3 animate-pulse rounded bg-white/5" />
        <div className="grid grid-cols-3 gap-6">
          <div className="h-32 animate-pulse rounded-lg bg-white/5" />
          <div className="h-32 animate-pulse rounded-lg bg-white/5" />
          <div className="h-32 animate-pulse rounded-lg bg-white/5" />
        </div>
      </div>
    </div>
  );
}
